import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import {
  TrendingUp, DollarSign, ShoppingCart, Package,
  Calendar, Store, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export default function StoreAnalytics() {
  const { token } = useAuth();
  const [period, setPeriod] = useState('week');
  const [comparison, setComparison] = useState(null);
  const [trends, setTrends] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, trendRes, topRes, storeRes] = await Promise.all([
        axios.get(`${API_URL}/api/stores/analytics/comparison?period=${period}`, { headers }),
        axios.get(`${API_URL}/api/stores/analytics/trends?days=${period === 'today' ? 1 : period === 'week' ? 7 : 30}`, { headers }),
        axios.get(`${API_URL}/api/stores/analytics/top-products?period=${period}`, { headers }),
        axios.get(`${API_URL}/api/stores`, { headers }),
      ]);
      setComparison(compRes.data);
      setTrends(trendRes.data);
      setTopProducts(topRes.data);
      setStores(storeRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build chart data for comparison
  const comparisonChartData = comparison?.stores?.map(s => ({
    name: s.store_name.length > 15 ? s.store_name.substring(0, 15) + '...' : s.store_name,
    Revenue: s.total_revenue,
    Sales: s.total_sales,
    'Avg Ticket': s.avg_ticket,
  })) || [];

  // Build trend chart data
  const trendChartData = [];
  if (trends?.trends) {
    const storeNames = {};
    stores.forEach(s => { storeNames[s.id] = s.name; });

    Object.entries(trends.trends).sort(([a], [b]) => a.localeCompare(b)).forEach(([date, storeData]) => {
      const point = { date: date.substring(5) }; // MM-DD format
      Object.entries(storeData).forEach(([sid, data]) => {
        const name = storeNames[sid] || sid.substring(0, 8);
        point[name] = data.revenue;
      });
      trendChartData.push(point);
    });
  }

  const storeKeys = stores.map(s => s.name);

  // Total stats
  const totalRevenue = comparison?.stores?.reduce((sum, s) => sum + s.total_revenue, 0) || 0;
  const totalSales = comparison?.stores?.reduce((sum, s) => sum + s.total_sales, 0) || 0;
  const bestStore = comparison?.stores?.reduce((best, s) => s.total_revenue > (best?.total_revenue || 0) ? s : best, null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="store-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">Store Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Compare performance across all locations</p>
        </div>
        <div className="flex gap-2" data-testid="period-selector">
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                period === p.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="summary-cards">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-emerald-500" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Revenue</span>
          </div>
          <p className="text-2xl font-bold text-emerald-500">${totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Sales</span>
          </div>
          <p className="text-2xl font-bold text-blue-500">{totalSales}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Store className="w-4 h-4 text-purple-500" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Active Stores</span>
          </div>
          <p className="text-2xl font-bold text-purple-500">{stores.length}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Top Store</span>
          </div>
          <p className="text-lg font-bold text-amber-500 truncate">
            {bestStore?.store_name || 'N/A'}
          </p>
          {bestStore && (
            <p className="text-xs text-muted-foreground">${bestStore.total_revenue.toLocaleString()}</p>
          )}
        </div>
      </div>

      {/* Revenue Comparison Chart */}
      <div className="bg-card border border-border rounded-xl p-5" data-testid="comparison-chart">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-500" />
          Revenue Comparison
        </h2>
        {comparisonChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={comparisonChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="name" tick={{ fill: 'currentColor', fontSize: 12 }} className="text-muted-foreground" />
              <YAxis tick={{ fill: 'currentColor', fontSize: 12 }} className="text-muted-foreground" />
              <Tooltip
                contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--foreground))' }}
              />
              <Legend />
              <Bar dataKey="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Sales" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
            No sales data for this period. Complete some POS transactions to see comparisons.
          </div>
        )}
      </div>

      {/* Trend Chart */}
      {trendChartData.length > 1 && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="trend-chart">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-500" />
            Daily Revenue Trends
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="date" tick={{ fill: 'currentColor', fontSize: 12 }} className="text-muted-foreground" />
              <YAxis tick={{ fill: 'currentColor', fontSize: 12 }} className="text-muted-foreground" />
              <Tooltip
                contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--foreground))' }}
              />
              <Legend />
              {storeKeys.map((key, i) => (
                <Line key={key} type="monotone" dataKey={key} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 4 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Store-by-Store Breakdown */}
      <div className="bg-card border border-border rounded-xl p-5" data-testid="store-breakdown">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Store className="w-5 h-5 text-emerald-500" />
          Store Breakdown
        </h2>
        {comparison?.stores?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground text-left">
                  <th className="px-4 py-3 font-medium">Store</th>
                  <th className="px-4 py-3 font-medium text-right">Revenue</th>
                  <th className="px-4 py-3 font-medium text-right">Sales</th>
                  <th className="px-4 py-3 font-medium text-right">Avg Ticket</th>
                  <th className="px-4 py-3 font-medium text-right">Items Sold</th>
                </tr>
              </thead>
              <tbody>
                {comparison.stores.map((store, idx) => (
                  <tr key={store.store_id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                        <span className="text-foreground font-medium">{store.store_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-500 font-semibold">${store.total_revenue.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    <td className="px-4 py-3 text-right text-foreground">{store.total_sales}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">${store.avg_ticket.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{store.total_items}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm text-center py-6">No stores to compare yet. Add stores from the Store Management page.</p>
        )}
      </div>

      {/* Top Products per Store */}
      {topProducts?.by_store && Object.keys(topProducts.by_store).length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="top-products">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-500" />
            Top Selling Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(topProducts.by_store).map(([storeId, products]) => {
              const storeName = stores.find(s => s.id === storeId)?.name || storeId;
              return (
                <div key={storeId} className="bg-muted/30 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">{storeName}</h3>
                  {products.map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-5">{idx + 1}.</span>
                        <span className="text-sm text-foreground">{p.product_name}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-muted-foreground">{p.quantity_sold} sold</span>
                        <span className="text-emerald-500 font-medium">${p.revenue.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                  {products.length === 0 && (
                    <p className="text-xs text-muted-foreground">No sales data</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
